import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { listEstimates, updateEstimateStatus } from "@/lib/estimates.functions";
import { useState } from "react";
import { Loader2, ExternalLink, Phone, Mail, MapPin, Settings, FileText } from "lucide-react";
import { SectionTabs, CRM_TABS } from "@/components/admin/SectionTabs";

const STATUSES = ["new", "contacted", "scheduled", "won", "lost"] as const;
type Status = typeof STATUSES[number];

const STATUS_STYLES: Record<Status, string> = {
  new: "bg-amber-300/15 text-amber-200 border-amber-300/30",
  contacted: "bg-blue-400/10 text-blue-300 border-blue-400/30",
  scheduled: "bg-purple-400/10 text-purple-300 border-purple-400/30",
  won: "bg-green-400/10 text-green-300 border-green-400/30",
  lost: "bg-zinc-700/40 text-zinc-400 border-white/10",
};

export const Route = createFileRoute("/_authenticated/admin/estimates")({
  head: () => ({ meta: [{ title: "AI Estimates — Admin" }] }),
  component: AdminEstimates,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="py-32 text-center text-zinc-300 bg-[#030303] min-h-screen">
        <p className="mb-3">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
  notFoundComponent: () => <div className="py-32 text-center text-zinc-400">Not found.</div>,
});

function AdminEstimates() {
  const fetchList = useServerFn(listEstimates);
  const updateStatus = useServerFn(updateEstimateStatus);
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Status | "all">("all");

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "estimates"],
    queryFn: () => fetchList(),
  });

  const mut = useMutation({
    mutationFn: (vars: { id: string; status: Status }) => updateStatus({ data: vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "estimates"] }),
  });

  const rows = (data ?? []).filter((r) => filter === "all" || r.status === filter);

  return (
    <section className="min-h-screen bg-[#030303] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-28 lg:pb-24">
        <div className="mb-6"><SectionTabs tabs={CRM_TABS} label="CRM" /></div>
        <header className="mb-6 sm:mb-8 flex items-end justify-between flex-wrap gap-3 sm:gap-4">
          <div>
            <p className="text-[10px] sm:text-[11px] uppercase tracking-widest text-amber-200 mb-1.5 sm:mb-2">Admin</p>
            <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl">AI Estimates</h1>
            <p className="text-zinc-500 text-xs sm:text-sm mt-1">{data?.length ?? 0} total · {data?.filter((r) => r.status === "new").length ?? 0} new</p>
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <Link to="/admin/ai-usage" className="flex-1 sm:flex-none justify-center inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-xs text-zinc-300 hover:text-amber-200 hover:border-amber-300/30">
              AI Usage
            </Link>
            <Link to="/admin/pricing" className="flex-1 sm:flex-none justify-center inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-xs text-zinc-300 hover:text-amber-200 hover:border-amber-300/30">
              <Settings className="size-3.5" /> Pricing Settings
            </Link>
          </div>
        </header>

        <div className="flex gap-2 mb-5 sm:mb-6 overflow-x-auto no-scrollbar -mx-1 px-1 sm:flex-wrap">
          <button onClick={() => setFilter("all")} className={chipCls(filter === "all")}>All</button>
          {STATUSES.map((st) => (
            <button key={st} onClick={() => setFilter(st)} className={`${chipCls(filter === st)} shrink-0`}>
              {st[0].toUpperCase() + st.slice(1)} <span className="opacity-60">({data?.filter((r) => r.status === st).length ?? 0})</span>
            </button>
          ))}
        </div>

        {isLoading && <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="size-4 animate-spin" /> Loading…</div>}
        {error && <p className="text-red-400 text-sm">{error.message.includes("Forbidden") ? "You're signed in, but this account isn't an admin. Ask an existing admin to grant you the 'admin' role in user_roles." : error.message}</p>}

        <div className="space-y-3">
          {rows.map((r) => (
            <article key={r.id} className="border border-white/10 rounded-2xl bg-white/[0.02] p-4 sm:p-5">
              <div className="flex items-start justify-between flex-wrap gap-3 sm:gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center flex-wrap gap-2 sm:gap-3 mb-2">
                    <h3 className="font-serif text-lg sm:text-xl truncate">{r.name}</h3>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${STATUS_STYLES[r.status as Status]}`}>{r.status}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:flex-wrap gap-x-5 gap-y-1.5 text-xs text-zinc-400">
                    <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1.5 hover:text-white"><Mail className="size-3" /> {r.email}</a>
                    <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1.5 hover:text-white"><Phone className="size-3" /> {r.phone}</a>
                    {(r.address || r.city) && <span className="inline-flex items-center gap-1.5"><MapPin className="size-3" /> {[r.address, r.city].filter(Boolean).join(", ")}</span>}
                  </div>
                </div>
                <div className="text-left sm:text-right w-full sm:w-auto">
                  <p className="font-serif text-xl sm:text-2xl text-amber-200">${r.price_low?.toLocaleString()} – ${r.price_high?.toLocaleString()}</p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">{r.estimated_gallons} gal · {r.estimated_labor_hours} hrs · {r.estimated_duration_days} day{(r.estimated_duration_days ?? 0) > 1 ? "s" : ""}</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mt-4 pt-4 border-t border-white/5 text-xs">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Project</p>
                  <p className="text-zinc-300">{r.project_type} · {r.number_of_coats} coats · {r.paint_quality}</p>
                  {formatRooms(r.rooms) && <p className="text-zinc-500 mt-1">{formatRooms(r.rooms)}</p>}
                  {formatSurfaces(r) && <p className="text-zinc-500 mt-1">Includes: {formatSurfaces(r)}</p>}
                  {((r.exterior_surfaces as string[] | null) ?? []).length > 0 && (
                    <p className="text-zinc-500 mt-1">{((r.exterior_surfaces as string[]) ?? []).join(", ")}</p>
                  )}
                  {r.square_footage && <p className="text-zinc-500 mt-1">{r.square_footage} sqft</p>}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Colors</p>
                  <div className="flex flex-wrap gap-1.5">
                    {((r.selected_colors as Array<{ hex: string; name: string }>) ?? []).slice(0, 8).map((c, i) => (
                      <span key={i} title={c.name} className="size-5 rounded border border-white/10" style={{ backgroundColor: c.hex }} />
                    ))}
                    {((r.selected_colors as Array<unknown>) ?? []).length === 0 && <span className="text-zinc-400">No design attached</span>}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Design</p>
                  {r.design_image_url ? (
                    <a href={r.design_image_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-amber-200 hover:text-amber-100">
                      View rendered version <ExternalLink className="size-3" />
                    </a>
                  ) : <span className="text-zinc-400">—</span>}
                </div>
              </div>

              {(r.notes || r.repairs_needed || r.timeline) && (
                <div className="mt-4 pt-4 border-t border-white/5 text-xs text-zinc-400 space-y-1">
                  {r.timeline && <p><span className="text-zinc-500">Timeline:</span> {r.timeline}</p>}
                  {r.repairs_needed && <p><span className="text-zinc-500">Repairs:</span> {r.repairs_needed}</p>}
                  {r.notes && <p><span className="text-zinc-500">Notes:</span> {r.notes}</p>}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-white/5 flex flex-wrap items-center gap-1.5 sm:gap-2">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1 w-full sm:w-auto">Set status:</span>
                {STATUSES.map((st) => (
                  <button
                    key={st}
                    disabled={mut.isPending || r.status === st}
                    onClick={() => mut.mutate({ id: r.id, status: st })}
                    className={`text-[11px] px-2.5 py-1.5 rounded-full border transition min-h-[36px] ${r.status === st ? STATUS_STYLES[st] : "border-white/10 text-zinc-400 hover:text-white hover:border-white/20"}`}
                  >{st}</button>
                ))}
                <Link
                  to="/admin/estimates/$id/proposal"
                  params={{ id: r.id }}
                  className="sm:ml-auto inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full bg-amber-300 text-zinc-950 font-semibold hover:bg-amber-200 min-h-[36px]"
                >
                  <FileText className="size-3.5" /> Generate proposal
                </Link>
                <span className="text-[10px] text-zinc-400 w-full sm:w-auto sm:text-right">{new Date(r.created_at).toLocaleString()}</span>
              </div>
            </article>
          ))}
          {!isLoading && rows.length === 0 && (
            <p className="text-zinc-500 text-sm text-center py-12">No estimate requests yet.</p>
          )}
        </div>
      </div>
    </section>
  );
}

const chipCls = (active: boolean) =>
  `text-xs px-3 py-1.5 rounded-full border transition ${active ? "border-amber-300/60 bg-amber-300/10 text-amber-100" : "border-white/10 bg-white/[0.02] text-zinc-400 hover:text-white"}`;

type RoomLike = { type?: string; floorSqft?: number; length?: number; width?: number; ceilingHeight?: number; doorCount?: number; windowCount?: number };

function formatRooms(rooms: unknown): string {
  if (!Array.isArray(rooms) || rooms.length === 0) return "";
  // Collapse identical entries (e.g. synthesized "Whole home" split)
  const parts: string[] = [];
  const counts = new Map<string, number>();
  for (const raw of rooms as RoomLike[]) {
    if (!raw || typeof raw !== "object") continue;
    const type = raw.type ?? "Room";
    const sqft = raw.floorSqft ?? (raw.length && raw.width ? raw.length * raw.width : undefined);
    const key = `${type}|${sqft ?? ""}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const totalSqft = (rooms as RoomLike[]).reduce((sum, r) => {
    const s = r?.floorSqft ?? (r?.length && r?.width ? r.length * r.width : 0);
    return sum + (s || 0);
  }, 0);
  for (const [key, n] of counts) {
    const [type, sqft] = key.split("|");
    parts.push(`${n > 1 ? `${n}× ` : ""}${type}${sqft ? ` (${Math.round(Number(sqft))} sqft${n > 1 ? " ea" : ""})` : ""}`);
  }
  const summary = parts.join(", ");
  return totalSqft > 0 && (rooms as RoomLike[]).length > 1
    ? `${summary} — ${Math.round(totalSqft)} sqft total`
    : summary;
}

function formatSurfaces(r: Record<string, unknown>): string {
  const flags: Array<[keyof typeof r | string, string]> = [
    ["includes_trim", "trim"],
    ["includes_doors", "doors"],
    ["includes_cabinets", "cabinets"],
    ["includes_ceilings", "ceilings"],
  ];
  return flags.filter(([k]) => r[k as string] === true).map(([, l]) => l).join(", ");
}